import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Heart, Building2, CreditCard, QrCode, FileCheck, Shield } from 'lucide-react';

const Donate = () => {
  const bankDetails = {
    bankName: 'Bank Of India',
    accountName: 'Vallalar Velai Vaaipu Sevai Iyakkam',
    accountNumber: '810220110000215',
    ifsc: 'BKID0008102',
    micrCode: '605013016',
    branch: 'Thavalakuppam'
  };

  const trustDocuments = [
    {
      title: '12AA Form',
      description: 'Our Trust has 12AA Registration',
      image: '/assets/vvvsi-images/donate-12aa.jpg',
      icon: FileCheck
    },
    {
      title: '80G Form',
      description: 'Tax Exemption Certificate',
      image: '/assets/vvvsi-images/donate-80g.jpg',
      icon: Shield
    },
    {
      title: 'CSR Code',
      description: 'Corporate Social Responsibility Approval',
      image: '/assets/vvvsi-images/donate-80g.jpg',
      icon: Building2
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-slate-50 to-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              We Accept Donations To Serve For Our Society
            </h1>
            <p className="text-xl text-rose-100 max-w-3xl mx-auto">
              Your contribution helps us provide employment services, career guidance, education support, and medical assistance to those in need.
            </p>
            <div className="mt-6 inline-block bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
              <p className="text-lg font-semibold">
                You're Eligible to get Tax Exemption For Your Donations Through 80G Form
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Bank Details & QR Code */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Bank Account Details */}
            <Card className="p-8 border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    Bank Account Details
                  </h2>
                  <p className="text-slate-500 text-sm">Transfer your donation directly</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-600">Bank Name</span>
                  <span className="font-semibold text-slate-900">{bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between items-start py-3 border-b border-slate-100">
                  <span className="text-slate-600">Account Name</span>
                  <span className="font-semibold text-slate-900 text-right max-w-[200px]">{bankDetails.accountName}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-600">Account Number</span>
                  <span className="font-semibold text-slate-900 font-mono">{bankDetails.accountNumber}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-600">IFSC Code</span>
                  <span className="font-semibold text-slate-900 font-mono">{bankDetails.ifsc}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-600">MICR Code</span>
                  <span className="font-semibold text-slate-900 font-mono">{bankDetails.micrCode}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-600">Branch</span>
                  <span className="font-semibold text-slate-900">{bankDetails.branch}</span>
                </div>
              </div>
            </Card>

            {/* QR Code */}
            <Card className="p-8 border-slate-200 flex flex-col items-center justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    Scan QR Code
                  </h2>
                  <p className="text-slate-500 text-sm">Quick payment via UPI</p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-slate-100">
                <img 
                  src="/assets/vvvsi-images/donate-qrcode.jpg" 
                  alt="Donation QR Code"
                  className="w-64 h-64 object-contain"
                />
              </div>
              <p className="mt-4 text-slate-600 text-center text-sm">
                Scan this QR code with any UPI app to make your donation
              </p>
            </Card>
          </div>

          {/* Trust Documents */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Outfit' }}>
              Trust Account Details & Certificates
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {trustDocuments.map((doc, index) => (
                <Card key={index} className="p-6 border-slate-200 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <doc.icon className="w-7 h-7 text-rose-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{doc.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{doc.description}</p>
                  <div className="rounded-lg overflow-hidden border border-slate-200">
                    <img 
                      src={doc.image} 
                      alt={doc.title}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Why Donate */}
          <Card className="p-8 border-slate-200 bg-gradient-to-br from-rose-50 to-white">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Outfit' }}>
              Your Donation Helps Us
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Employment Services</h3>
                <p className="text-sm text-slate-600">Job fairs, notifications & facilitation centers</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🎓</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Education Support</h3>
                <p className="text-sm text-slate-600">Scholarships & educational resources</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🏥</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Medical Assistance</h3>
                <p className="text-sm text-slate-600">Healthcare support for the needy</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🌱</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Career Guidance</h3>
                <p className="text-sm text-slate-600">Mentorship & career counseling programs</p>
              </div>
            </div>
          </Card>

          {/* Contact for Donations */}
          <div className="mt-12 text-center">
            <p className="text-slate-600">
              For any queries regarding donations, please contact us at{' '}
              <a href="mailto:info@vvvsi.com" className="text-rose-600 font-medium hover:underline">
                info@vvvsi.com
              </a>{' '}
              or call{' '}
              <a href="tel:+918270527992" className="text-rose-600 font-medium hover:underline">
                +91 82705 27992
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Donate;
